import React, { createContext, useContext, useEffect, useState } from 'react';
import { products as staticProducts } from '../data/mockProducts';
import { getProducts } from '../lib/shopify';

const ProductsContext = createContext();

export const useProducts = () => {
  const context = useContext(ProductsContext);
  if (!context) {
    throw new Error('useProducts must be used within a ProductsProvider');
  }
  return context;
};

// Merge live Shopify data (images, price, description, stock) onto the curated
// local product metadata (features, care, cross-sells, colourway, etc.).
// Curated fields stay in code; only the dynamic fields are overridden by Shopify.
const mergeProduct = (meta, shopifyProduct) => {
  if (!shopifyProduct) return meta;
  return {
    ...meta,
    name: shopifyProduct.name || meta.name,
    price: typeof shopifyProduct.price === 'number' ? shopifyProduct.price : meta.price,
    currency: shopifyProduct.currency || meta.currency,
    images: shopifyProduct.images && shopifyProduct.images.length ? shopifyProduct.images : meta.images,
    shortDescription: shopifyProduct.shortDescription || meta.shortDescription,
    descriptionHtml: shopifyProduct.descriptionHtml || meta.descriptionHtml,
    inStock: typeof shopifyProduct.inStock === 'boolean' ? shopifyProduct.inStock : meta.inStock,
    // Live Shopify variants/options drive checkout variant resolution.
    variants: shopifyProduct.variants || meta.variants,
    options: shopifyProduct.options || meta.options,
  };
};

// Build a product purely from Shopify data for products that have no curated
// metadata in code (i.e. brand-new products added in the Shopify admin).
// These render auto-generated PDPs and check out using their live variant IDs.
const buildFromShopify = (sp) => ({
  ...sp,
  tagline: sp.tagline || (sp.shortDescription ? sp.shortDescription.slice(0, 100) : ''),
  // No curated category yet -> appears under "All Products" in the shop.
  category: sp.category || undefined,
  collection: sp.collection || undefined,
  featured: sp.featured || false,
});

export const ProductsProvider = ({ children }) => {
  // Start with the local data so the UI renders instantly, then sync from Shopify.
  const [products, setProducts] = useState(staticProducts);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    getProducts()
      .then((shopifyProducts) => {
        if (!mounted) return;
        const shopBySlug = {};
        shopifyProducts.forEach((sp) => {
          shopBySlug[sp.slug] = sp;
        });

        const merged = [];
        const seen = new Set();

        // Curated products first (preserves intended ordering), merged with live data.
        staticProducts.forEach((meta) => {
          merged.push(mergeProduct(meta, shopBySlug[meta.slug]));
          seen.add(meta.slug);
        });

        // Append any Shopify products that have no curated metadata (new products).
        shopifyProducts.forEach((sp) => {
          if (!seen.has(sp.slug)) {
            merged.push(buildFromShopify(sp));
          }
        });

        setProducts(merged);
      })
      .catch(() => {
        // Gracefully fall back to local data if Shopify is unreachable.
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const getProductBySlug = (slug) => products.find((p) => p.slug === slug);
  const getProductsByCategory = (category) => products.filter((p) => p.category === category);
  const getFeaturedProducts = () => products.filter((p) => p.featured);

  return (
    <ProductsContext.Provider
      value={{ products, loading, getProductBySlug, getProductsByCategory, getFeaturedProducts }}
    >
      {children}
    </ProductsContext.Provider>
  );
};
